import React, { useState, useContext, useRef, useEffect } from 'react';
import 'react-perfect-scrollbar/dist/css/styles.css';
import { ThemeProvider as MuiThemeProvider, StyledEngineProvider } from '@mui/material';
import { ThemeProvider } from "styled-components"
import GlobalStyles from './components/GlobalStyles';
import theme from './theme';
import { Route, Routes, Navigate, useLocation, matchPath } from 'react-router-dom';

import { PopupNotifProvider } from "./contexts/PopupNotifContext";
import { LanguageProvider } from "./contexts/LanguageContext"
import { SocketContext, socket } from "./contexts/SocketContext"
import { UserAdminClientProvider } from "./contexts/UserAdminClientContext"
import UserContext, { UserProvider } from "./contexts/UserContext"
import SuperAdminContext, { SuperAdminProvider } from "./contexts/SuperAdminContext"
import { FormeAdresseApprenant } from "./contexts/FormeAdresseContext"

import DashboardLayout from './components/DashboardLayout';
import { navbarMasquee, masqueParEcran } from './utils/navbarMasquee';
import MainLayout from './components/MainLayout';

import AdminDashboard from './pages/AdminDashboard'

import OverlayForSave from './components/ui/Admin/OverlayForSave'

import Login from './pages/Login'
import ErrorLogin from './pages/ErrorLogin'
import Register from './pages/Register'
import RegisterAdmin from './pages/RegisterAdmin'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import NotFound from './pages/NotFound'
import OnBoarding from './pages/OnBoarding'
import Dashboard from './pages/Dashboard'
import MyCertif from './pages/MyCertif'
import Formation from './pages/Formation'
import NoAccess from './pages/NoAccess'

import Groups from "./pages/Admin/Groups/Groups"
import Support from "./pages/Admin/Support/Support"
import Group from "./pages/Admin/Groups/Group"
import NewGroup from "./pages/Admin/Groups/NewGroup"
import Ecoles from "./pages/Admin/Ecoles/Ecoles"
import Ecole from "./pages/Admin/Ecoles/Ecole"
import NewEcole from "./pages/Admin/Ecoles/NewEcole"
import EcoleResults from "./pages/Admin/Ecoles/EcoleResults"
import AdminClients from "./pages/Admin/AdminClients/AdminClients"
import AdminClient from "./pages/Admin/AdminClients/AdminClient"
import NewAdminClient from "./pages/Admin/AdminClients/NewAdminClient"
import Formations from "./pages/Admin/Formations/Formations"
import NewFormation from "./pages/Admin/Formations/NewFormation"
import FormationEdit from "./pages/Admin/Formations/Formation"
import FormationPlayer from "./pages/Admin/Formations/FormationPlayer"

import Terms_FR from './pages/Terms_FR'
import Terms_EN from './pages/Terms_EN'


import { isLoggedIn, getCurrentUser, setUser } from "./utils/auth"
import { redirectionIcebreaker } from "./utils/icebreakerGuard"
import APIS, { GLB_URL_THESCAN } from "./utils/APIS"

function RequireAuth({ children }: { children: JSX.Element }) {
  let location = useLocation();
  let user = getCurrentUser()

  if (isLoggedIn() && (user?.type === "student" || user?.type === "theScanCandidat")) {
    return (
      <SocketContext.Provider value={socket}>
        <UserProvider>
          {/* Tutoiement / vouvoiement : la cible du module en cours (07/10/2026,
              contexts/FormeAdresseContext.js). */}
          <FormeAdresseApprenant>
            <LayoutApprenant chemin={location.pathname}>
              <StudentUserRoute>
                {children}
              </StudentUserRoute>
            </LayoutApprenant>
          </FormeAdresseApprenant>
        </UserProvider>
      </SocketContext.Provider>
    );
  } else {
    return <Navigate to="/" state={{ from: location }} />;
  }
}

/* Menu du haut : masque par l'ecran, ou tant que l'onboarding n'est pas
   termine, juge sur le profil du serveur des qu'il est charge
   (utils/navbarMasquee.js, C9 du 06/10/2026). */
const LayoutApprenant = ({ chemin, children }) => {
  const { currentUser, allLoaded } = useContext(UserContext)
  const hideNavbar = navbarMasquee({
    masqueParEcran: masqueParEcran(chemin),
    profilServeur: allLoaded ? currentUser : null,
    profilGarde: getCurrentUser(),
  })
  return <DashboardLayout hideNavbar={hideNavbar}>{children}</DashboardLayout>
}

const StudentUserRoute = ({children}) => {
  let location = useLocation();
  const { currentUser, allLoaded, actPlan } = useContext(UserContext)

  if (allLoaded) {
    if (currentUser && currentUser?.type === "student") {
      if (currentUser?.isOnboarded === true) {
        let icebreakerPlan = actPlan[0]
        if (currentUser.isAdmin === true) {
          icebreakerPlan.isDone = true
        }
        // Lot C (18/09) : l'icebreaker reste OBLIGATOIRE avant une formation,
        // mais n'enferme plus l'utilisateur partout. La decision d'aiguillage
        // est dans utils/icebreakerGuard (testee isolement). La croix de
        // l'icebreaker ramene au dashboard (FormatsHelpers TopBar), sans
        // deconnexion ni modification de isDone (quitter != terminer).
        const cibleIcebreaker = redirectionIcebreaker(icebreakerPlan.isDone, location.pathname)
        if (cibleIcebreaker) {
          return <Navigate to={cibleIcebreaker} state={{ from: location }} />;
        }
        return (
          <>{children}</>
        )
      } else if (currentUser?.isOnboarded !== true && location.pathname === "/dashboard") {
        return (
          <OnBoarding />
        )
      } else if (currentUser?.isOnboarded !== true && location.pathname !== "/dashboard") {
        return (
          <Navigate to="/dashboard" state={{ from: location }} />
        )
      }
    } else if (currentUser && currentUser?.type === "theScanCandidat") {
      if (location?.pathname?.includes("/thescan/")) {
        return (
          <>{children}</>
        )
      } else {
        return <Navigate to="/" state={{ from: location }} />;
      }
    } else if (!currentUser) {
      return (<></>)
    }
  } else {
    return (<></>)
  }
}

function RequireAuthSuperAdmin({ children }: { children: JSX.Element }) {
  let location = useLocation();
  let user = getCurrentUser()

  if (isLoggedIn() && user?.type === "student") {
    return (
      <SocketContext.Provider value={socket}>
        <UserProvider>
          <SuperAdminProvider>
            <DashboardLayout menuforadmin="true">
              <SuperAdminRoute>
                {children}
              </SuperAdminRoute>
            </DashboardLayout>
          </SuperAdminProvider>
        </UserProvider>
      </SocketContext.Provider>
    );
  } else {
    return <Navigate to="/" state={{ from: location }} />;
  }
}

const SuperAdminRoute = ({children}) => {
  let location = useLocation();
  const { currentUser, allLoaded, actPlan } = useContext(UserContext)
  const { overlayForSave } = useContext(SuperAdminContext)

  if (allLoaded && currentUser) {
    if (currentUser?.type === "student" && currentUser?.isAdmin === true) {
      return (
        <>
          {children}
          {overlayForSave &&
            <OverlayForSave />
          }
        </>
      )
    } else {
      return (<Navigate to="/" state={{ from: location }} />)
    }
  } else {
    return (<></>)
  }
}

function delayed_render(async_fun, deps=[]) {
    const [output, setOutput] = useState()
    useEffect(() => {
      // wrap your async call here
      const loadData = async () => {
        setOutput(await async_fun())
      };
      // then call it here
      loadData();
    }, deps);
    return (output === undefined) ? null : output
}

function RequireAuthClientAdmin({ children }: { children: JSX.Element }) {
  let location = useLocation();
  let user = getCurrentUser()
  if (isLoggedIn() && user?.type === "adminclient") {
    return (
      <SocketContext.Provider value={socket}>
        <UserAdminClientProvider>
          <DashboardLayout>
            {children}
          </DashboardLayout>
        </UserAdminClientProvider>
      </SocketContext.Provider>
    );
  } else {
    return <Navigate to="/" state={{ from: location }} />;
  }
}

function GoToHome({ children }: { children: JSX.Element }) {
  let location = useLocation();
  let user = getCurrentUser()
  if (isLoggedIn()) {
    if (user?.type === "student") {
      return <Navigate to="/dashboard" state={{ from: location }} />;
    } else if (user?.type === "adminclient") {
      return <Navigate to="/admindashboard" state={{ from: location }} />;
    } else if (user?.type === "theScanRecruiter" || user?.type === "theScanCandidat") {
      return delayed_render(async () => {
        let result = await APIS.getLoginStatus()
        if (result?.message == "status" && result?.user) {
          let confirmUser = result?.user
          if (confirmUser?.type === "theScanRecruiter") {
            window.location.href = GLB_URL_THESCAN;
          } else if (confirmUser?.type === "theScanCandidat") {
            const matchPathname = matchPath({
              path: "/thescan/:id",
              exact: true,
              strict: false
            }, window?.location?.pathname);
            if (matchPathname && matchPathname?.params?.id) {
              return (
                <RequireAuth>
                  <Formation />
                </RequireAuth>
              )
            } else {
              window.location.href = GLB_URL_THESCAN;
            }
          }
        }
      })
    } else {
      return <Navigate to="/" state={{ from: location }} />;
    }
  } else {
    const arriveSurUnScan = matchPath({ path: "/thescan/:id" }, location?.pathname)
    if (arriveSurUnScan && arriveSurUnScan?.params?.id) {
      return delayed_render(async () => {
        const result = await APIS.getLoginStatus()
        if (result?.message == "status" && result?.user?.type === "theScanCandidat") {
          // on adopte la session serveur cote THE LAB puis on ouvre le scan
          setUser(result.user)
          return (
            <RequireAuth>
              <Formation />
            </RequireAuth>
          )
        }
        window.location.href = GLB_URL_THESCAN;
        return (<></>)
      })
    }
    return <Navigate to="/login" state={{ from: location }} />;
  }
}

function GoTo404({ children }: { children: JSX.Element }) {
  let location = useLocation();
  return <Navigate to="/404" state={{ from: location }} />;
}

function AlwaysLoginRedirect({ children }: { children: JSX.Element }) {
  let location = useLocation();
  if (isLoggedIn()) {
    return <Navigate to="/" state={{ from: location }} />;
  }

  return children;
}

function NoLoginRedirect({ children }: { children: JSX.Element }) {
  let location = useLocation();
  if (!isLoggedIn()) {
    return <Navigate to="/" state={{ from: location }} />;
  }

  return children;
}

const App = () => {
  let location = useLocation();

  return (
    <StyledEngineProvider injectFirst>
      <MuiThemeProvider theme={theme}>
        <ThemeProvider theme={theme}>
          <LanguageProvider>
            <PopupNotifProvider>
              <GlobalStyles />
              <Routes>
                <Route exact path="/" element={
                  <GoToHome/>
                }/>

                <Route exact path="/terms_FR" element={<Terms_FR/>}/>
                <Route exact path="/terms_EN" element={<Terms_EN/>}/>


                <Route exact path='/login' element={
                  <AlwaysLoginRedirect>
                    <MainLayout>
                      <Login/>
                    </MainLayout>
                  </AlwaysLoginRedirect>
                }/>
                <Route exact path='/errorLogin' element={
                  <AlwaysLoginRedirect>
                    <MainLayout>
                      <ErrorLogin/>
                    </MainLayout>
                  </AlwaysLoginRedirect>
                }/>
                <Route exact path='/forgotPassword' element={
                  <AlwaysLoginRedirect>
                    <MainLayout>
                      <ForgotPassword/>
                    </MainLayout>
                  </AlwaysLoginRedirect>
                }/>
                <Route exact path='/resetPassword' element={
                <AlwaysLoginRedirect>
                  <MainLayout>
                    <ResetPassword/>
                  </MainLayout>
                </AlwaysLoginRedirect>
              }/>
                <Route exact path='/register' element={
                  <AlwaysLoginRedirect>
                    <MainLayout>
                      <Register/>
                    </MainLayout>
                  </AlwaysLoginRedirect>
                }/>
                <Route exact path='/registerAdmin' element={
                  <AlwaysLoginRedirect>
                    <MainLayout>
                      <RegisterAdmin/>
                    </MainLayout>
                  </AlwaysLoginRedirect>
                }/>
                <Route exact path='/404' element={
                  <MainLayout>
                    <NotFound/>
                  </MainLayout>
                }/>


                <Route exact path="/admindashboard/*" element={
                  <RequireAuthClientAdmin>
                    <AdminDashboard />
                  </RequireAuthClientAdmin>
                }/>
                <Route exact path="/noAccess" element={
                  <NoLoginRedirect>
                    <MainLayout>
                      <NoAccess/>
                    </MainLayout>
                  </NoLoginRedirect>
                }/>
                <Route exact path="/dashboard" element={
                  <RequireAuth>
                    <Dashboard />
                  </RequireAuth>
                }/>
                <Route exact path="/myCertification" element={
                  <RequireAuth>
                    <MyCertif />
                  </RequireAuth>
                }/>
                <Route exact path="/formation" element={
                  <RequireAuth>
                    <Formation />
                  </RequireAuth>
                }/>
                <Route exact path="/icebreaker" element={
                  <RequireAuth>
                    <Formation />
                  </RequireAuth>
                }/>
                <Route exact path="/thescan" element={
                  <GoToHome/>
                }/>
                <Route exact path="/thescan/:id" element={
                  <GoToHome/>
                }/>

                <Route exact path="/admin" element={
                  <Navigate to="/admin/groupe" state={{ from: location }} />
                }/>
                <Route exact path="/admin/groupe" element={
                  <RequireAuthSuperAdmin>
                    <Groups />
                  </RequireAuthSuperAdmin>
                }/>
                <Route exact path="/admin/support" element={
                  <RequireAuthSuperAdmin>
                    <Support />
                  </RequireAuthSuperAdmin>
                }/>
                <Route exact path="/admin/groupe/add" element={
                  <RequireAuthSuperAdmin>
                    <NewGroup />
                  </RequireAuthSuperAdmin>
                }/>
                <Route exact path="/admin/groupe/edit/:id" element={
                  <RequireAuthSuperAdmin>
                    <Group />
                  </RequireAuthSuperAdmin>
                }/>
                <Route exact path="/admin/formation" element={
                  <RequireAuthSuperAdmin>
                    <Formations />
                  </RequireAuthSuperAdmin>
                }/>
                <Route exact path="/admin/formation/add" element={
                  <RequireAuthSuperAdmin>
                    <NewFormation />
                  </RequireAuthSuperAdmin>
                }/>
                <Route exact path="/admin/formation/player/:id" element={
                  <RequireAuthSuperAdmin>
                    <FormationPlayer />
                  </RequireAuthSuperAdmin>
                }/>
                <Route exact path="/admin/formation/edit/:id" element={
                  <RequireAuthSuperAdmin>
                    <FormationEdit />
                  </RequireAuthSuperAdmin>
                }/>
                <Route exact path="/admin/ecoles" element={
                  <RequireAuthSuperAdmin>
                    <Ecoles />
                  </RequireAuthSuperAdmin>
                }/>
                <Route exact path="/admin/ecoles/add" element={
                  <RequireAuthSuperAdmin>
                    <NewEcole />
                  </RequireAuthSuperAdmin>
                }/>
                <Route exact path="/admin/ecoles/edit/:id" element={
                  <RequireAuthSuperAdmin>
                    <Ecole />
                  </RequireAuthSuperAdmin>
                }/>
                <Route exact path="/admin/ecoles/results/:id" element={
                  <RequireAuthSuperAdmin>
                    <EcoleResults />
                  </RequireAuthSuperAdmin>
                }/>
                <Route exact path="/admin/adminclient" element={
                  <RequireAuthSuperAdmin>
                    <AdminClients />
                  </RequireAuthSuperAdmin>
                }/>
                <Route exact path="/admin/adminclient/add" element={
                  <RequireAuthSuperAdmin>
                    <NewAdminClient />
                  </RequireAuthSuperAdmin>
                }/>
                <Route exact path="/admin/adminclient/edit/:id" element={
                  <RequireAuthSuperAdmin>
                    <AdminClient />
                  </RequireAuthSuperAdmin>
                }/>

                <Route exact path='*' element={
                  <GoTo404/>
                }/>
              </Routes>
            </PopupNotifProvider>
          </LanguageProvider>
        </ThemeProvider>
      </MuiThemeProvider>
    </StyledEngineProvider>
  );
};

export default App;
